// ============================================================================
// Formato de arquivo dos desenhos — compatível com o plugin Excalidraw do
// Obsidian, nos DOIS formatos que ele entende.
// ----------------------------------------------------------------------------
// `.excalidraw`     JSON puro. É o "modo de compatibilidade" do plugin (formato
//                   legado, que ele continua abrindo e editando). É também o
//                   que o excalidraw.com importa/exporta direto. → PADRÃO AQUI.
//
// `.excalidraw.md`  Markdown com o JSON num bloco cercado. É o que o plugin
//                   cria por padrão hoje. Lemos e gravamos também, para um
//                   vault criado no Obsidian abrir aqui sem conversão.
//
// O conteúdo do desenho é sempre o MESMO JSON; muda só o invólucro. Por isso o
// app guarda internamente o JSON e decide o invólucro na hora de gravar, a
// partir da extensão do arquivo.
//
// Compressão: o plugin do Obsidian tem a opção "Compress Excalidraw JSON", que
// grava o desenho num bloco ```compressed-json com o JSON comprimido por
// lz-string (compressToBase64) e quebrado em várias linhas. Aqui a gente
// descomprime e abre normalmente — nas 3 plataformas, já que é o mesmo bundle.
// ============================================================================
// Import padrão (CJS): o import nomeado funciona no Vite mas quebra no Node,
// onde roda o sinapse-agent (tools/sync-cli).
import LZString from 'lz-string'
const { decompressFromBase64 } = LZString

export type CenaExcalidraw = {
  type: 'excalidraw'
  version: number
  source: string
  elements: unknown[]
  appState: Record<string, unknown>
  files: Record<string, unknown>
}

export const FONTE = 'https://app.getsinapse.com'

/** Cena vazia — ponto de partida de um desenho novo. */
export function cenaVazia(): CenaExcalidraw {
  return {
    type: 'excalidraw',
    version: 2,
    source: FONTE,
    elements: [],
    appState: { gridSize: null, viewBackgroundColor: 'transparent' },
    files: {},
  }
}

// --- Extensões ---------------------------------------------------------------
export const EXT_JSON = '.excalidraw'
export const EXT_MD = '.excalidraw.md'

/** O arquivo é um desenho? (qualquer um dos dois formatos) */
export function ehArquivoDeDesenho(nome: string): boolean {
  return /\.excalidraw(\.md)?$/i.test(nome.trim())
}

/** Formato de um caminho/título de desenho. */
export function formatoDe(nome: string): 'md' | 'json' {
  return /\.excalidraw\.md$/i.test(nome.trim()) ? 'md' : 'json'
}

/** Título "limpo", sem a extensão, para mostrar na interface. */
export function tituloLimpo(nome: string): string {
  return nome.replace(/\.excalidraw(\.md)?$/i, '').trim()
}

// --- Leitura -----------------------------------------------------------------

/** Erro com mensagem útil quando o arquivo é um desenho mas não dá para abrir. */
export class DesenhoIlegivel extends Error {}

/** Extrai a cena do conteúdo de um arquivo, seja ele JSON puro ou markdown do
 *  plugin do Obsidian. Lança `DesenhoIlegivel` com explicação quando não dá. */
export function lerCena(conteudo: string): CenaExcalidraw {
  const texto = (conteudo ?? '').trim()
  if (!texto) return cenaVazia()

  // (a) JSON puro (.excalidraw)
  if (texto.startsWith('{')) return normalizar(parseJson(texto))

  // (b) Markdown do plugin: procura o bloco de dados do desenho.
  //     O plugin grava ```json (padrão) ou ```compressed-json (opcional).
  const comprimido = /```compressed-json\s*\n([\s\S]*?)```/i.exec(texto)
  if (comprimido) {
    // O plugin quebra o base64 em várias linhas (com linhas em branco no meio):
    // tira TODO espaço/quebra antes de descomprimir.
    const limpo = comprimido[1].replace(/\s+/g, '')
    const json = limpo ? decompressFromBase64(limpo) : ''
    if (json) return normalizar(parseJson(json))
    throw new DesenhoIlegivel(
      'Este desenho foi salvo comprimido pelo Obsidian e não consegui descomprimir os dados.',
    )
  }
  const bloco = /```json\s*\n([\s\S]*?)```/i.exec(texto)
  if (bloco) return normalizar(parseJson(bloco[1]))

  // (c) Markdown sem bloco reconhecível — talvez um .excalidraw.md vazio.
  if (/excalidraw-plugin/i.test(texto)) return cenaVazia()

  throw new DesenhoIlegivel('Não encontrei os dados do desenho neste arquivo.')
}

function parseJson(texto: string): Record<string, unknown> {
  try {
    return JSON.parse(texto) as Record<string, unknown>
  } catch (e) {
    throw new DesenhoIlegivel(`Dados do desenho inválidos: ${(e as Error).message}`)
  }
}

/** Garante os campos que o componente do Excalidraw espera. */
function normalizar(obj: Record<string, unknown>): CenaExcalidraw {
  const base = cenaVazia()
  return {
    ...base,
    ...obj,
    type: 'excalidraw',
    elements: Array.isArray(obj.elements) ? (obj.elements as unknown[]) : [],
    appState: { ...base.appState, ...((obj.appState as object) ?? {}) },
    files: ((obj.files as Record<string, unknown>) ?? {}) as Record<string, unknown>,
  }
}

// --- Escrita -----------------------------------------------------------------

/** Serializa a cena no formato pedido. `titulo` só é usado no invólucro .md. */
export function gravarCena(cena: CenaExcalidraw, formato: 'md' | 'json', titulo = ''): string {
  const limpa = paraArquivo(cena)
  const json = JSON.stringify(limpa, null, 2)
  if (formato === 'json') return json + '\n'
  return montarMarkdownDoObsidian(json, limpa, titulo)
}

/** Tira do appState o que é estado de SESSÃO (seleção, zoom momentâneo,
 *  ferramenta ativa). Sem isso, mexer no canvas sem desenhar nada já marcaria o
 *  arquivo como alterado e dispararia sync a cada clique. */
function paraArquivo(cena: CenaExcalidraw): CenaExcalidraw {
  const {
    collaborators: _c,
    selectedElementIds: _s,
    selectedGroupIds: _g,
    editingGroupId: _e,
    cursorButton: _cb,
    activeTool: _at,
    ...appState
  } = (cena.appState ?? {}) as Record<string, unknown>
  return {
    type: 'excalidraw',
    version: 2,
    source: FONTE,
    elements: cena.elements ?? [],
    appState,
    files: cena.files ?? {},
  }
}

/** Invólucro markdown no formato que o plugin do Obsidian lê:
 *  frontmatter `excalidraw-plugin: parsed`, a lista de textos (para busca) e o
 *  bloco `## Drawing` com o JSON. */
function montarMarkdownDoObsidian(json: string, cena: CenaExcalidraw, titulo: string): string {
  const textos = (cena.elements as { type?: string; text?: string }[])
    .filter((el) => el?.type === 'text' && typeof el.text === 'string' && el.text.trim())
    .map((el) => el.text!.trim())

  return [
    '---',
    '',
    'excalidraw-plugin: parsed',
    'tags: [excalidraw]',
    '',
    '---',
    '',
    '==⚠  Switch to EXCALIDRAW VIEW in the MORE OPTIONS menu of this document. ⚠==',
    '',
    ...(titulo ? [`# ${titulo}`, ''] : []),
    '# Excalidraw Data',
    '',
    '## Text Elements',
    ...(textos.length ? textos : []),
    '',
    '## Drawing',
    '```json',
    json,
    '```',
    '%%',
    '',
  ].join('\n')
}

// --- Utilidades para a interface --------------------------------------------

/** Resumo curto para a lista de notas (o corpo é JSON, não serve de snippet). */
export function resumoDoDesenho(conteudo: string): string {
  try {
    const cena = lerCena(conteudo)
    const n = cena.elements.length
    if (!n) return 'desenho vazio'
    const textos = (cena.elements as { type?: string; text?: string }[])
      .filter((el) => el?.type === 'text' && el.text?.trim())
      .map((el) => el.text!.trim())
    const inicio = textos.length ? ` · ${textos.join(' ').slice(0, 34)}` : ''
    return `${n} elemento${n > 1 ? 's' : ''}${inicio}`
  } catch {
    return 'desenho'
  }
}
